import { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stPoolConfig,
  stUpdatePoolConfig,
  StPoolConfig,
} from '../../../services/socialTradingService';
import { stText } from '../StListUi';

export default function SocialTradingPoolConfigList() {
  const t = useTranslate();
  const notify = useNotify();
  const [cfg, setCfg] = useState<StPoolConfig | null>(null);
  const [minVolume, setMinVolume] = useState('');
  const [minProfit, setMinProfit] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await stPoolConfig();
      setCfg(data || null);
      setMinVolume(data?.minVolumeParticipation ?? '');
      setMinProfit(data?.minProfitParticipation ?? '');
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      const body: { minVolumeParticipation?: string; minProfitParticipation?: string } = {};
      if (minVolume.trim() !== '') body.minVolumeParticipation = minVolume.trim();
      if (minProfit.trim() !== '') body.minProfitParticipation = minProfit.trim();
      const next = await stUpdatePoolConfig(body);
      setCfg(next || null);
      setMinVolume(next?.minVolumeParticipation ?? minVolume);
      setMinProfit(next?.minProfitParticipation ?? minProfit);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  return (
    <PermissionGuard resource="st-pool-config" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.poolConfig')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.poolConfig') },
          ]}
          action={
            <Button size="small" variant="outlined" onClick={load}>
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />
        <Stack spacing={2} sx={{ mt: 2, maxWidth: 640 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('module.socialTrading.poolConfigParticipation')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('module.socialTrading.poolConfigHint')}
              </Typography>
              {cfg && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('module.socialTrading.poolConfigCurrent', {
                    volume: stText(cfg.minVolumeParticipation),
                    profit: stText(cfg.minProfitParticipation),
                  })}
                </Typography>
              )}
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label={t('module.socialTrading.poolFieldMinVolumeParticipation')}
                  value={minVolume}
                  onChange={(e) => setMinVolume(e.target.value)}
                />
                <TextField
                  size="small"
                  label={t('module.socialTrading.poolFieldMinProfitParticipation')}
                  value={minProfit}
                  onChange={(e) => setMinProfit(e.target.value)}
                />
                <PermissionButton resource="st-pool-config" action="update">
                  <Button variant="contained" onClick={save} sx={{ alignSelf: 'flex-start' }}>
                    {t('module.socialTrading.save')}
                  </Button>
                </PermissionButton>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
}
